import { ChartSeriesResponse, GeographyResponse } from 'common/store/api/chart.api';
import {
  buildLineChartDataFromSeries,
  buildPieChartDataFromSeries,
  newBuildBarChartDataFromSeries,
} from '../../utils';
import {
  PPTChartData,
  PPTDataProcessorResult,
  PPTGeographyRecord,
  PPTProcessorParameters,
  SankeyResponse,
  WordcloudResponse,
} from '../../../../models/pptx.model';
import {
  BarChartSeriesDataItem,
  BarChartSeriesItem,
  CategoryItem,
  ExtendedKendoSeries,
  KwelloBarChartOptions,
  KwelloLineChartOptions,
  LineChartSeriesItem,
} from '../../../../models/chart.model';
import { format, intervalToDuration } from 'date-fns';
import { Datum } from '../../../../store/api/chart.api';
import { DATE_FORMAT, ISO_DATE_REGEX, MONTH_YEAR_FORMAT } from '../../../../constants';
import { formatAppDate } from '../../../../hooks/utils';
import { Data } from '../../widget-hooks/useExcelExportStore';
import {
  DEFAULT_BARCHART_OPTIONS,
  DEFAULT_LINECHART_OPTIONS,
} from '../../../../contexts/useWidgetOptionsContext';

export const PPTbuildBarChartData = ({
  data,
  chartOptions,
  appliedFilter,
  widget,
}: PPTProcessorParameters<ChartSeriesResponse>): PPTDataProcessorResult => {
  const widgetData = data;
  const chartOptionsWithDefaults: KwelloBarChartOptions = {
    ...DEFAULT_BARCHART_OPTIONS,
    ...widget?.barChartOptions,
    ...chartOptions?.barChartOptions,
  };
  const interval = intervalToDuration({
    start: new Date(appliedFilter.startDate),
    end: new Date(appliedFilter.endDate),
  });
  const converted = newBuildBarChartDataFromSeries(widgetData.groupedList, [], '', {
    valueType: chartOptionsWithDefaults.valueType,
    seriesSortOrder: chartOptionsWithDefaults.seriesSortOrder,
    seriesValueDisplayType: chartOptionsWithDefaults.seriesValueDisplayType,
    formatCategoryLabel(item: Datum) {
      const value = item.x;
      const isDate =
        typeof value === 'string' && ISO_DATE_REGEX.test(value) && !isNaN(Date.parse(value));
      if (isDate) {
        return formatAppDate(
          new Date(item.x),
          (interval?.months ?? 0) <= 3 ? DATE_FORMAT : MONTH_YEAR_FORMAT
        );
      }
      return item?.tooltip ?? item.x.toString();
    },
  });
  const result = converted.series.map(
    (series: BarChartSeriesItem & ExtendedKendoSeries): PPTChartData => {
      const categories =
        chartOptionsWithDefaults.direction === 'vertical'
          ? converted.categories
          : [...converted.categories].reverse(); // pptxgenjs bottom-to-top

      const valueTypeStr = Array.isArray(chartOptionsWithDefaults.valueType)
        ? chartOptionsWithDefaults.valueType[0]
        : chartOptionsWithDefaults.valueType;

      const points = categories.map((c: CategoryItem) => {
        const item = series.data.find((d: BarChartSeriesDataItem) => d.categoryId === c.id);

        // Use `value` for percentage-mode bar sizing, while `displayLabels` carry
        // the user-facing percentages patched into the generated PPTX XML.
        let v;
        if (valueTypeStr === 'percentage') {
          v = item?.value;
        } else {
          v = item?.dataCount;
        }
        if (v == null) v = item?.value ?? 0;
        const value = Number.isFinite(v) ? v : 0;

        const displayLabel =
          valueTypeStr === 'percentage' && value !== 0
            ? `${parseFloat(Number(item?.displayValue ?? item?.value ?? 0).toFixed(1))}%`
            : '';

        return { label: c.name, value, displayLabel };
      });

      const isPercentage = valueTypeStr === 'percentage';
      return {
        name: series.name,
        color: series.color1 ?? undefined,
        labels: points.map((p) => p.label),
        values: points.map((p) => p.value).map((v) => (v === 0 ? NaN : v)), // <- no filtering; 1:1 with labels
        ...(isPercentage && { displayLabels: points.map((p) => p.displayLabel) }),
      };
    }
  );
  return result;
};

export const PPTbuildSankeyChartData = ({
  data,
}: PPTProcessorParameters<SankeyResponse>): PPTDataProcessorResult => {
  const sankeyData = data;
  const previousAlignment: Record<string, number> = {};
  const latestAlignment: Record<string, number> = {};

  for (const groupedItem of sankeyData.groupedList || []) {
    if (groupedItem?.data) {
      for (const data of groupedItem.data || []) {
        if (data?.x != null && data?.y != null) {
          previousAlignment[String(data.x)] = data.y ?? 0;

          for (const dd of data.data || []) {
            if (dd?.x != null && dd?.y != null) {
              const key = String(dd.x);
              latestAlignment[key] = (previousAlignment[key] ?? 0) + dd.y;
            }
          }
        }
      }
    }
  }
  return { image: sankeyData.image, previousAlignment, latestAlignment };
};

export const PPTbuildWordcloudData = ({
  data,
}: PPTProcessorParameters<WordcloudResponse>): PPTDataProcessorResult => {
  const wordcloudData = data;
  const words = (wordcloudData.data as Data[])
    .filter((item: Data) => item?.aggregates?.Count && item?.aggregates?.Count > 0)
    .map((item: Data) => ({
      text: item.name,
      value: item.aggregates.Count ?? 0,
      id: item.id,
    }));
  return { image: wordcloudData.image, words: words };
};

export const PPTbuildDoughnutChartData = ({
  data,
}: PPTProcessorParameters<ChartSeriesResponse>): PPTDataProcessorResult => {
  const response = data;
  if (!response.groupedList?.length) return [];
  const { series, seriesLabel } = buildPieChartDataFromSeries(response.groupedList);

  const name = seriesLabel ?? 'Series'; // e.g., 'specialtyName'

  const labels = series.map((item) => item.x);
  const values = series.map((d) => d.y ?? 0);

  return [
    {
      name,
      labels,
      values,
    },
  ];
};

export const PPTbuildLineChartData = ({
  data,
  chartOptions,
  widget,
}: PPTProcessorParameters<ChartSeriesResponse>): PPTDataProcessorResult => {
  const chartOptionsWithDefaults: KwelloLineChartOptions = {
    ...DEFAULT_LINECHART_OPTIONS,
    ...widget?.lineChartOptions,
    ...chartOptions?.lineChartOptions,
  };

  const converted = buildLineChartDataFromSeries(data);
  const result = converted.series.map((series: LineChartSeriesItem): PPTChartData => {
    const categories = converted.categories;
    return {
      name: widget.labels?.[series.name] ?? series.name,
      labels: categories.map((c) =>
        chartOptionsWithDefaults?.dateFormat
          ? format(c.toString(), chartOptionsWithDefaults?.dateFormat)
          : c.toString()
      ),
      values: series.data.map((d) => d ?? 0),
    };
  });
  return result;
};

export const PPTbuildGeographyChartData = ({
  data,
}: PPTProcessorParameters<GeographyResponse>): PPTDataProcessorResult => {
  if (!data?.features?.length) return [];
  return data.features.map((feature): PPTGeographyRecord => {
    return {
      country: feature.properties.name,
      value: feature.properties.dataCount ?? 0,
    };
  });
};
